import { mapByTemplate } from '@utils/dto.mapper'
import {
  DocumentsDefaultSettings,
  Scanner,
  ScanSettings,
} from '../services/ScannerService'
import { FileTypeEnum } from './interfaces'

export type colorMode = 'Color' | 'Grayscale' | 'BlackAndWhite'
export type PaperSource = 'Flatbed' | 'Feeder' | 'duplex'
export type PageSize = 'A4' | 'Letter' | 'Legal'
export type Outputformat = 'application/pdf' | 'image/jpeg' | 'image/png'

export interface SettingsValidation {
  isFullySupported: boolean
  warnings: string[]
  reducedSettings: ScanSettings
}

export function finddpiOrNearest(
  targetdpi: number,
  Scannerdpi: number[],
): number {
  if (Scannerdpi.length === 0) return targetdpi
  return Scannerdpi.reduce((nearest, current) => {
    return Math.abs(current - targetdpi) < Math.abs(nearest - targetdpi)
      ? current
      : nearest
  })
}

export function getAvailablecolorMode(
  documentColor: colorMode,
  supportedColors: string[],
): colorMode {
  if (supportedColors.includes(documentColor)) return documentColor
  const colorMap: Record<colorMode, colorMode[]> = {
    Color: ['Grayscale', 'BlackAndWhite'],
    Grayscale: ['BlackAndWhite', 'Color'],
    BlackAndWhite: ['Grayscale', 'Color'],
  }
  for (const fallback of colorMap[documentColor]) {
    if (supportedColors.includes(fallback)) return fallback
  }
  return 'BlackAndWhite'
}

export function setAvailableSource(
  documentSource: PaperSource,
  scannerSources: Scanner['capabilities'] | null | undefined,
): PaperSource {
  if (!scannerSources) return documentSource
  const availablePaperSource: Record<PaperSource, boolean> = {
    Flatbed: scannerSources.supportsFlatbed ?? false,
    Feeder: scannerSources.supportsFeeder ?? false,
    duplex: scannerSources.supportsDuplex ?? false,
  }
  if (availablePaperSource[documentSource]) return documentSource
  const sourceMap: PaperSource[] = ['Feeder', 'Flatbed', 'duplex']

  for (const source of sourceMap) {
    if (availablePaperSource[source]) return source
  }
  return 'Flatbed'
}

export function ValidateAndReduceSettings(
  documentType: FileTypeEnum,
  scannerCaps: Scanner['capabilities'] | null | undefined,
): SettingsValidation {
  const documentSettings = DocumentsDefaultSettings[documentType]
  const warnings: string[] = []
  if (!scannerCaps) {
    return {
      isFullySupported: false,
      warnings: ['Impossible de vérifier les capacités du scanner'],
      reducedSettings: documentSettings,
    }
  }

  const reduceddpi = finddpiOrNearest(
    documentSettings.dpi,
    scannerCaps.dpiValues,
  )
  if (reduceddpi !== documentSettings.dpi) {
    warnings.push(
      `dpi ajusté de ${documentSettings.dpi} à ${reduceddpi} (non supporté par le scanner)`,
    )
  }

  const reducedcolorMode = getAvailablecolorMode(
    documentSettings.colorMode,
    scannerCaps.bitDepthValues,
  )
  if (reducedcolorMode !== documentSettings.colorMode) {
    warnings.push(
      `Mode couleur ajusté de ${documentSettings.colorMode} à ${reducedcolorMode} (non supporté par le scanner)`,
    )
  }

  const reducedSource = setAvailableSource(
    documentSettings.paperSource,
    scannerCaps,
  )
  if (reducedSource !== documentSettings.paperSource) {
    warnings.push(
      `Source papier ajustée de ${documentSettings.paperSource} à ${reducedSource} (non supporté par le scanner)`,
    )
  }

  return {
    isFullySupported: warnings.length === 0,
    warnings,
    reducedSettings: {
      ...documentSettings,
      dpi: reduceddpi,
      colorMode: reducedcolorMode,
      paperSource: reducedSource,
    },
  }
}

export function mergeSettings({
  documentType,
  Caps,
}: {
  documentType: FileTypeEnum
  Caps: Scanner['capabilities']
}): SettingsValidation {
  const documentSettings = DocumentsDefaultSettings[documentType]
  if (!Caps) {
    return {
      isFullySupported: false,
      warnings: ['Impossible de vérifier les capacités du scanner'],
      reducedSettings: documentSettings,
    }
  }
  const validation = ValidateAndReduceSettings(
    documentType,
    mapByTemplate(Caps, {
      dpiValues: [],
      bitDepthValues: [],
      supportsFlatbed: false,
      supportsFeeder: false,
      supportsDuplex: false,
      driver: '',
      maxWidth: 0,
      maxHeight: 0,
      model: '',
    }),
  )
  return validation
}
